import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import ServiceDetailsHeader from "../components/Common/ImageHeader";
import AtAGlance from "../components/ProductDetails/AtAGlance";
import CommunityGallery from "../components/ProductDetails/CommunityJoin";
import ExploreSection from "../components/ProductDetails/ExploreSection";
import ProductFeatures from "../components/ProductDetails/ProductFeatures";
import IdealFor from "../components/ProductDetails/IdealFor";
import KeyHighlights from "../components/ProductDetails/KeyHighLights";
import { productService } from "../services/productService";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
  HStack,
  Spinner,
} from "@chakra-ui/react";

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentProduct, setCurrentProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);

        // Always fetch from API to ensure we have complete data with all populated relations
        
        // Use the dedicated service function for complete product details
        const productResult = await productService.getProductDetails(id);
        
        if (productResult.success) {
          setCurrentProduct(productResult.data);
        } else {
          console.error('Failed to load product details:', productResult.error);
          setError("Failed to load product: " + productResult.error);
        }
      } catch (err) {
        console.error("Error loading product:", err);
        setError("Failed to load product: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, location.state]);

  // Loading state
  if (loading) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <Spinner size="xl" color="#000B8C" />
      </Box>
    );
  }

  // Error state or product not found
  if (error || !currentProduct) {
    return (
      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
      >
        <VStack spacing={6} textAlign="center" p={8}>
          <Text fontSize="2xl" fontWeight="bold" color="red.500">
            {error || "Product Not Found"}
          </Text>
          <Text fontSize="lg" color="gray.600">
            {error
              ? "Unable to load product details. Please try again later."
              : "The product you're looking for doesn't exist or has been moved."}
          </Text>
          <HStack spacing={4}>
            <Button colorScheme="blue" onClick={() => navigate("/")}>
              Back to Products
            </Button>
            <Button variant="outline" onClick={() => navigate("/")}>
              Go Home
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  const handleBreadcrumbClick = (href) => {
    if (href && href !== "#") {
      navigate(href);
    }
  };

  return (
    <>
      <ServiceDetailsHeader
        title={currentProduct.productHeader?.title || currentProduct.title}
        backgroundImage={currentProduct.productHeader?.headerImage}
        breadcrumbItems={[
          { label: "Home", href: "/" },
          { label: "Products", href: "/#products" },
          { label: currentProduct.title, href: "#", isCurrentPage: true },
        ]}
        onBreadcrumbClick={handleBreadcrumbClick}
      />

      <Box 
        bgImage="url('/png/background.png')"
        bgPosition="center"
        bgRepeat="no-repeat"
        bgSize="cover"
        bgAttachment="fixed"
        minH="100vh"
        position="relative"
      >
        <Container maxW="8xl" py={0} position="relative" zIndex={1}>
          <VStack spacing={12} align="stretch">
            {/* At A Glance Section */}
            <AtAGlance product={currentProduct} />

            {/* Product Features Section */}
            <ProductFeatures product={currentProduct} />

            {/* Ideal For Section */}
            {currentProduct.idealFor && currentProduct.idealFor.length > 0 && (
              <IdealFor product={currentProduct} />
            )}

            {/* Key Highlights Section */}
            {currentProduct.keyHighlights && currentProduct.keyHighlights.length > 0 && (
              <KeyHighlights product={currentProduct} />
            )}

            {/* Explore Section */}
            <ExploreSection product={currentProduct} />

            {/* Community Gallery Section */}
            {currentProduct.galleryImages && currentProduct.galleryImages.length > 0 && (
              <CommunityGallery product={currentProduct} />
            )}

          </VStack>
        </Container>
      </Box>
    </>
  );
};

export default ProductDetails;
